import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Radar, RefreshCw, Loader2, TrendingDown, TrendingUp, Search, ArrowRight,
  AlertTriangle, HelpCircle, ChevronRight, Clock, Sparkles, Bell, X, Eye,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// ── Sparkline (tiny inline chart of risk_score drift) ─────────────────
function Sparkline({ data, width = 60, height = 20, stroke = '#7c3aed' }) {
  const points = Array.isArray(data) ? data.filter(d => typeof (d?.risk_score) === 'number') : [];
  if (points.length < 2) return null;
  const scores = points.map(p => p.risk_score);
  const min = Math.min(0, ...scores);
  const max = Math.max(100, ...scores);
  const span = Math.max(1, max - min);
  const step = width / Math.max(1, points.length - 1);
  const yFor = (s) => height - ((s - min) / span) * (height - 2) - 1;
  const path = scores.map((s, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${yFor(s).toFixed(1)}`).join(' ');
  const last = scores[scores.length - 1];
  const first = scores[0];
  const trend = last > first ? 'up' : last < first ? 'down' : 'flat';
  const trendColor = trend === 'up' ? '#dc2626' : trend === 'down' ? '#059669' : '#6b7280';
  return (
    <svg width={width} height={height} className="inline-block" data-testid="vendor-watch-sparkline">
      {/* Threshold guides at score 40 and 70 */}
      <line x1={0} y1={yFor(70)} x2={width} y2={yFor(70)} stroke="#fecaca" strokeWidth="0.5" strokeDasharray="1 2" />
      <line x1={0} y1={yFor(40)} x2={width} y2={yFor(40)} stroke="#fde68a" strokeWidth="0.5" strokeDasharray="1 2" />
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Latest point highlighted */}
      <circle cx={(scores.length - 1) * step} cy={yFor(last)} r="2" fill={trendColor} />
    </svg>
  );
}

// ── Risk score helpers ────────────────────────────────────────────────
function riskTone(score) {
  if (score >= 70) return { bg: 'bg-red-50 border-red-200 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-300', label: 'High risk', icon: AlertTriangle };
  if (score >= 40) return { bg: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-300', label: 'Watch', icon: TrendingDown };
  return { bg: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-300', label: 'Stable', icon: TrendingUp };
}

function confidencePill(c) {
  const map = { high: 'bg-emerald-100 text-emerald-800', medium: 'bg-blue-100 text-blue-800', low: 'bg-gray-100 text-gray-700' };
  return map[c] || map.low;
}

// ── Brief detail panel ────────────────────────────────────────────────
function BriefDetail({ brief, onRegenerate, regenerating, token }) {
  const [history, setHistory] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!brief?.vendor_id) return;
    axios.get(`${BACKEND}/api/vendor-watch/history/${brief.vendor_id}?limit=12`, { headers })
      .then(r => setHistory(r.data.history || []))
      .catch(() => setHistory([]));
  }, [brief?.vendor_id, brief?.id, headers]);

  if (!brief) return null;
  const tone = riskTone(brief.risk_score);
  const RiskIcon = tone.icon;
  const hasHistory = history.length >= 2;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="vendor-watch-brief-detail">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{brief.vendor_name}</h2>
              <Badge variant="outline" className="text-[10px]">{brief.period}</Badge>
              <Badge variant="outline" className={`text-[10px] ${confidencePill(brief.confidence)}`}>{brief.confidence} confidence</Badge>
              {brief.kind === 'quarterly' && <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-800">Quarterly cron</Badge>}
              {brief.kind === 'manual' && <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-800">On-demand</Badge>}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Generated {new Date(brief.created_at).toLocaleString()} · by {brief.generated_by || '—'}
              {brief.deployment_stats && ` · Footprint: ${brief.deployment_stats.property_count}/${brief.deployment_stats.total_properties} properties`}
            </div>
          </div>
          <Button size="sm" onClick={onRegenerate} disabled={regenerating} className="gap-1.5 shrink-0" data-testid="vendor-watch-regenerate-btn">
            {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {regenerating ? 'Generating…' : 'Regenerate'}
          </Button>
        </div>

        {/* Risk score + sparkline */}
        <div className={`rounded-xl border p-4 ${tone.bg} flex items-center gap-4`}>
          <div className="h-14 w-14 rounded-full bg-white/70 border flex items-center justify-center shrink-0">
            <div className="text-center">
              <div className={`text-2xl font-bold leading-none ${tone.text}`}>{brief.risk_score}</div>
              <div className="text-[8px] uppercase tracking-widest text-muted-foreground mt-0.5">Risk</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold flex items-center gap-1.5 ${tone.text}`}>
              <RiskIcon className="h-3.5 w-3.5" /> {tone.label}
            </div>
            <p className="text-sm mt-0.5 text-foreground/80">{brief.executive_summary}</p>
          </div>
          {hasHistory && (
            <div className="shrink-0 pl-3 border-l border-black/10 dark:border-white/10 text-right" data-testid="vendor-watch-detail-sparkline">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Last {history.length} · drift</div>
              <Sparkline data={history} width={140} height={36} />
              <div className="text-[9px] text-muted-foreground mt-0.5">
                {history[0]?.period || '—'} → {history[history.length-1]?.period || '—'}
              </div>
            </div>
          )}
        </div>

        {/* Sections */}
        {brief.financial_signals?.length > 0 && (
          <Section title="Financial signals" items={brief.financial_signals} />
        )}
        {brief.product_changes?.length > 0 && (
          <Section title="Product & roadmap changes" items={brief.product_changes} />
        )}
        {brief.competitive_landscape?.length > 0 && (
          <Section title="Competitive landscape" items={brief.competitive_landscape} />
        )}
        {brief.renewal_risk && (
          <div>
            <h3 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Renewal risk</h3>
            <p className="text-sm leading-relaxed text-foreground/90 rounded-lg border bg-card p-3">{brief.renewal_risk}</p>
          </div>
        )}
        {brief.questions_to_ask?.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Questions to ask before renewal</h3>
            <ul className="space-y-1.5">
              {brief.questions_to_ask.map((q, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <HelpCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-violet-500" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {brief.fallback_reason && (
          <div className="text-[11px] text-muted-foreground italic border-t pt-3">
            AI unavailable: {brief.fallback_reason}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function Section({ title, items }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">{title}</h3>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm">
            <ChevronRight className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Vendor picker (for on-demand generation) ─────────────────────────
function VendorPickerDialog({ open, onOpenChange, token, onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/vendors?limit=200${q ? `&search=${encodeURIComponent(q)}` : ''}`, { headers })
      .then(r => setItems(r.data.vendors || r.data.items || r.data || []))
      .catch(() => toast.error('Failed to load vendors'))
      .finally(() => setLoading(false));
  }, [open, q, headers]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => onOpenChange(false)}>
      <div className="bg-background rounded-xl shadow-2xl border w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-2">
            <Radar className="h-4 w-4 text-violet-600" />
            <h3 className="text-sm font-semibold">Generate Vendor Watch brief</h3>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search vendors…" className="pl-8 h-8 text-xs" autoFocus />
          </div>
        </div>
        <ScrollArea className="flex-1 max-h-[400px]">
          <div className="p-1.5">
            {loading ? (
              <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : (
              items.slice(0, 100).map(v => (
                <button
                  key={v.id}
                  onClick={() => onPick(v)}
                  className="w-full text-left p-2 rounded-md hover:bg-muted flex items-center gap-2"
                  data-testid={`vendor-watch-pick-${v.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{v.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{v.website || '—'}</div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                </button>
              ))
            )}
            {!loading && items.length === 0 && (
              <div className="py-6 text-center text-xs text-muted-foreground">No vendors found.</div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function VendorWatch({ token }) {
  const [briefs, setBriefs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [briefsRes, alertsRes] = await Promise.all([
        axios.get(`${BACKEND}/api/vendor-watch/latest?limit=50`, { headers }),
        axios.get(`${BACKEND}/api/vendor-watch/alerts?limit=20`, { headers }),
      ]);
      setBriefs(briefsRes.data.items || []);
      setAlerts(alertsRes.data.items || []);
      if (!selectedId && briefsRes.data.items?.length > 0) setSelectedId(briefsRes.data.items[0].id);
    } catch (e) {
      toast.error('Failed to load briefs');
    } finally {
      setLoading(false);
    }
  }, [headers, selectedId]);

  useEffect(() => { load(); }, []);

  const dismissAlert = async (alertId) => {
    try {
      await axios.post(`${BACKEND}/api/vendor-watch/alerts/${alertId}/dismiss`, {}, { headers });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch { /* ignore */ }
  };

  const openBriefFromAlert = async (alert) => {
    try {
      await axios.post(`${BACKEND}/api/vendor-watch/alerts/${alert.id}/read`, {}, { headers });
    } catch { /* ignore */ }
    // Try to find the brief in current list, else refresh
    const b = briefs.find(x => x.id === alert.brief_id);
    if (b) setSelectedId(alert.brief_id);
    else load();
  };

  const generate = async (vendor) => {
    setPickerOpen(false);
    setRegenerating(true);
    try {
      const r = await axios.post(`${BACKEND}/api/vendor-watch/generate/${vendor.id}`, {}, { headers });
      toast.success(`Brief generated for ${vendor.name}`);
      setSelectedId(r.data.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setRegenerating(false);
    }
  };

  const regenerateSelected = async () => {
    const cur = briefs.find(b => b.id === selectedId);
    if (!cur) return;
    setRegenerating(true);
    try {
      const r = await axios.post(`${BACKEND}/api/vendor-watch/generate/${cur.vendor_id}`, {}, { headers });
      toast.success('Brief refreshed');
      setSelectedId(r.data.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  const selected = briefs.find(b => b.id === selectedId);

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="vendor-watch">
      {/* Risk-jump alert banner */}
      {alerts.length > 0 && (
        <div className="border-b bg-gradient-to-r from-amber-50 via-red-50 to-amber-50 dark:from-amber-950/30 dark:via-red-950/30 dark:to-amber-950/30 flex-shrink-0" data-testid="vendor-watch-alerts">
          <div className="px-4 py-2 flex items-start gap-2">
            <Bell className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-900 dark:text-amber-300">
                Risk-tier jumps · {alerts.length} alert{alerts.length === 1 ? '' : 's'}
              </div>
              <div className="space-y-1">
                {alerts.slice(0, 3).map(a => {
                  const isHigh = a.to_tier === 'high';
                  return (
                    <div key={a.id} className={`flex items-center gap-2 rounded-md px-2 py-1 ${isHigh ? 'bg-red-100/70 dark:bg-red-900/30' : 'bg-amber-100/70 dark:bg-amber-900/30'}`} data-testid={`vendor-watch-alert-${a.id}`}>
                      <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${isHigh ? 'text-red-700' : 'text-amber-700'}`} />
                      <div className="flex-1 min-w-0 text-xs">
                        <span className="font-semibold">{a.vendor_name}</span>
                        <span className="text-muted-foreground">
                          {' '}moved from <span className="uppercase font-medium">{a.from_tier}</span> ({a.from_score})
                          {' → '}
                          <span className={`uppercase font-bold ${isHigh ? 'text-red-700' : 'text-amber-700'}`}>{a.to_tier}</span> ({a.to_score})
                        </span>
                      </div>
                      <button
                        onClick={() => openBriefFromAlert(a)}
                        className="text-[10px] font-semibold text-primary hover:underline flex items-center gap-1 shrink-0"
                        data-testid={`vendor-watch-alert-view-${a.id}`}
                      >
                        <Eye className="h-2.5 w-2.5" /> View
                      </button>
                      <button
                        onClick={() => dismissAlert(a.id)}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                        title="Dismiss"
                        data-testid={`vendor-watch-alert-dismiss-${a.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                {alerts.length > 3 && (
                  <div className="text-[10px] text-muted-foreground pl-2">+{alerts.length - 3} more</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
      {/* Left: brief list */}
      <div className="w-72 border-r flex flex-col shrink-0 bg-card/30">
        <div className="p-3 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
              <Radar className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <div className="text-xs font-semibold">Vendor Watch</div>
              <div className="text-[9px] text-muted-foreground">Quarterly AI briefs</div>
            </div>
          </div>
          <Badge variant="outline" className="text-[9px] font-mono">{briefs.length}</Badge>
        </div>
        <div className="p-2 border-b">
          <Button size="sm" onClick={() => setPickerOpen(true)} className="w-full gap-1.5 h-8 text-[11px] bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700" data-testid="vendor-watch-new-brief-btn">
            <Sparkles className="h-3 w-3" /> New brief
          </Button>
        </div>
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : briefs.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              <Radar className="h-8 w-8 mx-auto opacity-30 mb-2" />
              No briefs yet. Click <b>New brief</b> to generate your first.
            </div>
          ) : (
            <div className="p-1.5 space-y-0.5">
              {briefs.map(b => {
                const tone = riskTone(b.risk_score);
                const hist = b.score_history || [];
                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedId(b.id)}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors ${selectedId === b.id ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/60 border border-transparent'}`}
                    data-testid={`vendor-watch-brief-${b.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{b.vendor_name}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {new Date(b.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          <span>·</span>
                          <span>{b.period}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 shrink-0">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tone.text} ${tone.bg}`}>{b.risk_score}</span>
                        {hist.length >= 2 && <Sparkline data={hist} width={50} height={16} />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Right: brief detail */}
      <div className="flex-1 min-w-0">
        {selected ? (
          <BriefDetail brief={selected} onRegenerate={regenerateSelected} regenerating={regenerating} token={token} />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center max-w-md p-8">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Radar className="h-7 w-7 text-white" />
              </div>
              <h2 className="text-lg font-bold tracking-tight mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor Watch</h2>
              <p className="text-sm mb-4">
                Quarterly AI briefs scan public information for every vendor in your stack — flagging renewal risk, product changes, financial signals, and competitive threats before your next contract conversation.
              </p>
              <Button onClick={() => setPickerOpen(true)} className="gap-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600" data-testid="vendor-watch-empty-cta">
                <Sparkles className="h-4 w-4" /> Generate your first brief
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>

      <VendorPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} token={token} onPick={generate} />
    </div>
  );
}
